// src/analytics/appsflyer.js
import { Capacitor } from "@capacitor/core";
import { AppsFlyer,AFConstants } from "appsflyer-capacitor-plugin";
import { getFirebaseAppInstanceId } from "./firebaseIdentity";
import { Preferences } from "@capacitor/preferences";

const isNative = () => Capacitor.isNativePlatform();
const getPlatform = () => (Capacitor.getPlatform ? Capacitor.getPlatform() : "web");
const isIOS = () => getPlatform() === "ios";

const MEDIA_SOURCE_KEY = "af_media_source";
const ATTRIBUTION_KEY =
  "af_first_touch_attribution";
const WEB_ATTRIBUTION_KEY =
  "af_web_first_touch_attribution";

const WEB_ATTRIBUTION_MAX_AGE_MS =
  30 * 24 * 60 * 60 * 1000;

let _inited = false;
let _initPromise = null;
let _lastCuid = null;
let _mediaSource = null;
let _attribution = null;
let _conversionListenerAdded = false;
let _deepLinkListenerAdded = false;

const PLACEHOLDER_SOURCES = new Set([
  null,
  "",
  "organic",
  "unknown",
  "direct",
  "firebase",
  "google.com",
  "apple.com",
]);

function toAfCuid(userInfo) {
  let raw = userInfo?.id;
  if (raw === null || raw === undefined) return null;

  raw = String(raw);

  // If caller already passed "user_84", don't double-prefix
  if (raw.startsWith("user_")) raw = raw.slice(5);

  const cuid = `user_${raw}`.replace(/[^a-zA-Z0-9_-]/g, "");
  return cuid.length ? cuid : null;
}

function normalizeMediaSource(raw) {
  const v = String(raw || "").trim();
  if (!v) return null;

  const lower = v.toLowerCase();

  // SatStacker Show
  if (
    lower === "satstacker_show" ||
    lower === "satstacker" ||
    lower === "satstacker_show_trial" ||
    lower.includes("satstacker_show") ||
    lower.includes("satstacker")
  ) {
    return "satstacker_show";
  }

  // Apple Ads / Apple Search Ads
  if (
    lower === "apple search ads" ||
    lower === "apple ads" ||
    lower === "iossearchads_int"
  ) {
    return "apple_search_ads";
  }

  return v;
}

function unwrapPayload(data) {
  // AppsFlyer callbacks can arrive as:
  // { data: {...} }, { deepLink: {...} }, { data: { deepLink: {...} } }, or a raw object.
  const root = data?.data || data || {};
  const deepLink = root?.deepLink || data?.deepLink || {};

  return {
    root,
    deepLink,
    merged: {
      ...root,
      ...deepLink,
    },
  };
}

function extractMediaSource(data) {
  const { merged } = unwrapPayload(data);

  const candidates = [
    merged.media_source,
    merged.af_media_source,
    merged.pid,
  ];

  for (const candidate of candidates) {
    const normalized =
      normalizeMediaSource(candidate);

    if (
      normalized &&
      !PLACEHOLDER_SOURCES.has(normalized)
    ) {
      return normalized;
    }
  }

  return null;
}

async function persistMediaSource(src) {
  const normalized = normalizeMediaSource(src);

  if (!normalized || PLACEHOLDER_SOURCES.has(normalized)) {
    console.log("[AF TEST] MEDIA SOURCE IGNORED", {
      raw: src,
      normalized,
    });
    return;
  }

  _mediaSource = normalized;

  try {
    await Preferences.set({
      key: MEDIA_SOURCE_KEY,
      value: normalized,
    });

    console.log(
      "[AF TEST] MEDIA SOURCE PERSISTED",
      normalized
    );
  } catch (e) {
    console.warn(
      "[AppsFlyer] failed to persist media_source",
      e
    );
  }
}

function firstAttributionValue(...values) {
  for (const value of values) {
    if (
      value === null ||
      value === undefined
    ) {
      continue;
    }

    const text = String(value).trim();

    if (text) {
      return text.slice(0, 512);
    }
  }

  return null;
}


function attributionBoolean(value) {
  if (value === true || value === "true") {
    return true;
  }

  if (value === false || value === "false") {
    return false;
  }

  return null;
}


function compactAttribution(values) {
  const out = {};

  for (const [key, value] of Object.entries(
    values
  )) {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      continue;
    }

    out[key] = value;
  }

  return out;
}


function mergeMissingAttribution(
  existing,
  incoming
) {
  const next = {
    ...(existing || {}),
  };

  for (const [key, value] of Object.entries(
    incoming || {}
  )) {
    if (
      next[key] === null ||
      next[key] === undefined ||
      next[key] === ""
    ) {
      next[key] = value;
    }
  }

  return next;
}


function buildAttribution(
  data,
  {
    touchType = "install",
    platform = getPlatform(),
  } = {}
) {
  const { merged } = unwrapPayload(data);

  const mediaSource = extractMediaSource(data);

  if (
    !mediaSource ||
    PLACEHOLDER_SOURCES.has(mediaSource)
  ) {
    return null;
  }

  return compactAttribution({
    version: 1,
    platform,
    touch_type: touchType,

    media_source: mediaSource,

    campaign: firstAttributionValue(
      merged.campaign,
      merged.c,
      merged.af_campaign
    ),

    campaign_id: firstAttributionValue(
      merged.campaign_id,
      merged.af_c_id,
      merged.af_campaign_id
    ),

    adset: firstAttributionValue(
      merged.adset,
      merged.af_adset
    ),

    adset_id: firstAttributionValue(
      merged.adset_id,
      merged.af_adset_id
    ),

    ad: firstAttributionValue(
      merged.ad,
      merged.af_ad
    ),

    ad_id: firstAttributionValue(
      merged.ad_id,
      merged.af_ad_id
    ),

    ad_type: firstAttributionValue(
      merged.ad_type,
      merged.af_ad_type
    ),

    channel: firstAttributionValue(
      merged.channel,
      merged.af_channel
    ),

    site_id: firstAttributionValue(
      merged.site_id,
      merged.af_siteid
    ),

    sub_site_id: firstAttributionValue(
      merged.sub_site_id,
      merged.af_sub_siteid
    ),

    click_id: firstAttributionValue(
      merged.clickid,
      merged.click_id,
      merged.af_click_id
    ),

    af_sub1: firstAttributionValue(
      merged.af_sub1
    ),

    af_sub2: firstAttributionValue(
      merged.af_sub2
    ),

    af_sub3: firstAttributionValue(
      merged.af_sub3
    ),

    af_sub4: firstAttributionValue(
      merged.af_sub4
    ),

    af_sub5: firstAttributionValue(
      merged.af_sub5
    ),

    deep_link_value: firstAttributionValue(
      merged.deep_link_value
    ),

    deep_link_sub1: firstAttributionValue(
      merged.deep_link_sub1
    ),

    deep_link_sub2: firstAttributionValue(
      merged.deep_link_sub2
    ),

    af_status: firstAttributionValue(
      merged.af_status
    ),

    is_first_launch: attributionBoolean(
      merged.is_first_launch
    ),

    is_retargeting: attributionBoolean(
      merged.is_retargeting
    ),

    retargeting_conversion_type:
      firstAttributionValue(
        merged.retargeting_conversion_type
      ),

    captured_at:
      new Date().toISOString(),
  });
}


async function persistAttribution(
  incoming
) {
  if (!incoming) return;

  try {
    let existing = _attribution;

    if (!existing) {
      const { value } =
        await Preferences.get({
          key: ATTRIBUTION_KEY,
        });

      if (value) {
        try {
          existing = JSON.parse(value);
        } catch {}
      }
    }

    const next =
      mergeMissingAttribution(
        existing,
        incoming
      );

    _attribution = next;

    await Preferences.set({
      key: ATTRIBUTION_KEY,
      value: JSON.stringify(next),
    });

    console.log(
      "[AppsFlyer] first-touch attribution persisted",
      next
    );
  } catch (e) {
    console.warn(
      "[AppsFlyer] failed to persist attribution",
      e
    );
  }
}


function buildWebAttribution(search = "") {
  if (isNative()) return null;

  const params =
    new URLSearchParams(search || "");

  const mediaSource =
    normalizeMediaSource(
      params.get("pid") ||
      params.get("af_media_source") ||
      params.get("utm_source")
    );

  if (
    !mediaSource ||
    PLACEHOLDER_SOURCES.has(mediaSource)
  ) {
    return null;
  }

  return compactAttribution({
    version: 1,
    platform: "web",
    touch_type: "web_click",

    media_source: mediaSource,

    campaign:
      firstAttributionValue(
        params.get("c"),
        params.get("campaign"),
        params.get("utm_campaign")
      ),

    campaign_id:
      firstAttributionValue(
        params.get("af_c_id")
      ),

    adset:
      firstAttributionValue(
        params.get("af_adset")
      ),

    adset_id:
      firstAttributionValue(
        params.get("af_adset_id")
      ),

    ad:
      firstAttributionValue(
        params.get("af_ad")
      ),

    ad_id:
      firstAttributionValue(
        params.get("af_ad_id")
      ),

    ad_type:
      firstAttributionValue(
        params.get("af_ad_type")
      ),

    channel:
      firstAttributionValue(
        params.get("af_channel"),
        params.get("utm_medium")
      ),

    site_id:
      firstAttributionValue(
        params.get("af_siteid")
      ),

    sub_site_id:
      firstAttributionValue(
        params.get("af_sub_siteid")
      ),

    click_id:
      firstAttributionValue(
        params.get("oppref"),
        params.get("clickid"),
        params.get("click_id")
      ),

    af_sub1:
      firstAttributionValue(
        params.get("af_sub1")
      ),

    af_sub2:
      firstAttributionValue(
        params.get("af_sub2")
      ),

    af_sub3:
      firstAttributionValue(
        params.get("af_sub3")
      ),

    af_sub4:
      firstAttributionValue(
        params.get("af_sub4")
      ),

    af_sub5:
      firstAttributionValue(
        params.get("af_sub5")
      ),

    captured_at:
      new Date().toISOString(),

    utm_content:
      firstAttributionValue(
        params.get("utm_content")
      ),

    utm_term:
      firstAttributionValue(
        params.get("utm_term")
      ),
  });
}


function readStoredWebAttribution() {
  if (
    typeof window === "undefined"
  ) {
    return null;
  }

  try {
    const raw =
      localStorage.getItem(
        WEB_ATTRIBUTION_KEY
      );

    if (!raw) return null;

    const parsed = JSON.parse(raw);

    const capturedAt =
      Date.parse(
        parsed?.captured_at || ""
      );

    if (
      Number.isFinite(capturedAt) &&
      Date.now() - capturedAt >
        WEB_ATTRIBUTION_MAX_AGE_MS
    ) {
      localStorage.removeItem(
        WEB_ATTRIBUTION_KEY
      );

      return null;
    }

    return parsed || null;
  } catch {
    return null;
  }
}


export function afCaptureWebAttribution(
  search = ""
) {
  if (isNative()) return null;

  const existing =
    readStoredWebAttribution();

  const incoming =
    buildWebAttribution(search);

  if (!incoming) {
    return existing;
  }

  const next =
    mergeMissingAttribution(
      existing,
      incoming
    );

  try {
    localStorage.setItem(
      WEB_ATTRIBUTION_KEY,
      JSON.stringify(next)
    );
  } catch {}

  return next;
}


export function afGetWebAttribution(
  search = ""
) {
  return afCaptureWebAttribution(
    search
  );
}

function withTimeout(promise, timeoutMs, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(`${label} timed out after ${timeoutMs}ms`));
      }, timeoutMs);
    }),
  ]);
}

export async function afSetUserId(id, { debug = false } = {}) {
  // Keep Login.jsx API: pass a string/number id
  return afSetCustomerUserId({ id }, { debug });
}

const AF_ENV = {
  devKey: import.meta.env.VITE_APPSFLYER_DEV_KEY,
  iosAppId: import.meta.env.VITE_APPSFLYER_IOS_APP_ID,
};

export async function afInit({ debug = false, devKey, iosAppId } = {}) {
  if (!isNative()) return false;

  if (_inited) return true;
  if (_initPromise) return _initPromise;

  _initPromise = (async () => {
    try {
      const resolvedDevKey = devKey ?? AF_ENV.devKey ?? null;
      const resolvedIosAppId = iosAppId ?? AF_ENV.iosAppId ?? null;

      console.log("[AppsFlyer] init config", {
        platform: isIOS() ? "ios" : "android",
        hasDevKey: !!resolvedDevKey,
        iosAppId: isIOS() ? resolvedIosAppId : undefined,
        debug: !!debug,
      });

      if (!resolvedDevKey) {
        console.warn("[AppsFlyer] Missing VITE_APPSFLYER_DEV_KEY");
        return false;
      }

      if (isIOS() && !resolvedIosAppId) {
        console.warn(
          "[AppsFlyer] Missing VITE_APPSFLYER_IOS_APP_ID (required on iOS)"
        );
        return false;
      }

      const initArgs = {
        devKey: String(resolvedDevKey),
        isDebug: !!debug,
        registerOnDeepLink: true,
        ...(isIOS()
          ? {
              appID: String(resolvedIosAppId),
            }
          : {}),
      };

      // -----------------------------------------
      // Conversion attribution listener
      // -----------------------------------------

      if (!_conversionListenerAdded) {
        try {
          await AppsFlyer.addListener(AFConstants.CONVERSION_CALLBACK, async (event) => {
            console.log(
              "[AF TEST] CONVERSION EVENT RAW",
              JSON.stringify(event ?? null)
            );

            if (
              event?.callbackName !== AFConstants.onConversionDataSuccess
            ) {
              console.log(
                "[AF TEST] CONVERSION EVENT IGNORED",
                event?.callbackName
              );
              return;
            }

            const data = event?.data ?? {};

            console.log(
              "[AF TEST] CONVERSION DATA RAW",
              JSON.stringify(data ?? null)
            );

            const { merged } = unwrapPayload(data);
            const src = extractMediaSource(data);

            const attribution =
              buildAttribution(
                data,
                {
                  touchType: "install",
                }
              );

            console.log("[AppsFlyer] conversion attribution", {
              media_source: merged.media_source,
              af_media_source: merged.af_media_source,
              pid: merged.pid,
              campaign: merged.campaign,
              c: merged.c,
              af_status: merged.af_status,
              is_first_launch: merged.is_first_launch,
              extracted_source: src,
              raw: data,
            });

            if (src) {
              await persistMediaSource(src);
            }

            if (attribution) {
              await persistAttribution(
                attribution
              );
            }
          });

          _conversionListenerAdded = true;
        } catch (e) {
          console.warn(
            "[AppsFlyer] conversion listener failed",
            e
          );
        }
      }

      // -----------------------------------------
      // Unified Deep Link listener
      // -----------------------------------------

      if (!_deepLinkListenerAdded) {
        try {
          await AppsFlyer.addListener(AFConstants.UDL_CALLBACK, async (res) => {
            console.log(
              "[AF TEST] UDL CALLBACK RAW",
              JSON.stringify(res ?? null)
            );

            if (res?.status !== "FOUND") {
              if (res?.status === "ERROR") {
                console.warn(
                  "[AppsFlyer] UDL error",
                  res?.error
                );
              }

              return;
            }

            const data = res?.deepLink ?? {};

            const { merged } = unwrapPayload(data);
            const src = extractMediaSource(data);

            const attribution =
              buildAttribution(
                data,
                {
                  touchType: "deep_link",
                }
              );

            console.log("[AppsFlyer] UDL attribution", {
              deep_link_value: merged.deep_link_value,
              deep_link_sub1: merged.deep_link_sub1,
              deep_link_sub2: merged.deep_link_sub2,
              media_source: merged.media_source,
              campaign: merged.campaign,
              extracted_source: src,
              raw: res,
            });

            if (src) {
              await persistMediaSource(src);
            }

            if (attribution) {
              await persistAttribution(
                attribution
              );
            }
          });

          _deepLinkListenerAdded = true;
        } catch (e) {
          console.warn(
            "[AppsFlyer] UDL listener failed",
            e
          );
        }
      }

      // -----------------------------------------
      // Start AppsFlyer
      // -----------------------------------------

      console.log("[AF TEST] BEFORE initSDK");

      try {
        const initRes = await withTimeout(
          AppsFlyer.initSDK(initArgs),
          5000,
          "AppsFlyer initSDK"
        );

        console.log(
          "[AF TEST] initSDK RESOLVED",
          JSON.stringify(initRes ?? null)
        );
      } catch (e) {
        /*
         * IMPORTANT:
         *
         * On the current Capacitor/AppsFlyer integration we've observed
         * initSDK() starting the native SDK successfully but its JS Promise
         * not resolving.
         *
         * Do NOT block the app because of that.
         */
        console.warn(
          "[AppsFlyer] initSDK did not resolve; continuing with native SDK",
          e
        );
      }

      /*
       * We have issued the native SDK initialization command.
       *
       * Do this BEFORE UID lookup so a UID problem also cannot leave the
       * rest of SatStacker permanently waiting on afInit().
       */
      _inited = true;

      console.log("[AF TEST] SDK START COMMAND COMPLETE");

      // -----------------------------------------
      // Get AppsFlyer UID — best effort only
      // -----------------------------------------

      try {
        console.log("[AF TEST] BEFORE getAppsFlyerUID");

        const uidRes = await withTimeout(
          AppsFlyer.getAppsFlyerUID(),
          3000,
          "AppsFlyer getAppsFlyerUID"
        );

        console.log(
          "[AF TEST] UID",
          JSON.stringify(uidRes ?? null)
        );
      } catch (e) {
        console.warn(
          "[AppsFlyer] UID unavailable; continuing",
          e
        );
      }

      // -----------------------------------------
      // Hydrate previously captured attribution
      // -----------------------------------------

      try {
        const { value } = await Preferences.get({
          key: MEDIA_SOURCE_KEY,
        });

        const normalized = normalizeMediaSource(value);

        console.log("[AF TEST] STORED MEDIA SOURCE", {
          raw: value,
          normalized,
        });

        if (normalized && !_mediaSource) {
          _mediaSource = normalized;
        }
      } catch (e) {
        console.warn(
          "[AppsFlyer] failed to hydrate stored media source",
          e
        );
      }

      console.log("[AF TEST] INIT FLOW COMPLETE");

      return true;
    } catch (e) {
      console.warn("[AppsFlyer] unexpected init failure", e);

      _inited = false;

      return false;
    } finally {
      /*
       * Only clear the promise after a genuine initialization failure.
       * That permits another call to afInit() to retry later.
       */
      if (!_inited) {
        _initPromise = null;
      }
    }
  })();

  return _initPromise;
}

/**
 * Set CUID from your userInfo in the exact format you want: user_${userInfo.id}
 */
export async function afSetCustomerUserId(
  userInfo,
  { debug = false } = {}
) {
  try {
    if (!isNative()) return;

    const cuid = toAfCuid(userInfo);

    if (!cuid) {
      console.warn("[AppsFlyer] No valid cuid");
      return;
    }

    if (_lastCuid === cuid) return;

    await afInit({ debug });

    console.log("[AppsFlyer] setting CUID", cuid);

    const res = await AppsFlyer.setCustomerUserId({ cuid });

    _lastCuid = cuid;

    console.log(
      "[AppsFlyer] setCustomerUserId complete",
      JSON.stringify(res ?? null),
      cuid
    );
  } catch (e) {
    console.warn("[AppsFlyer] setCustomerUserId failed", e);
  }
}

export async function afLogEvent(name, values = {}, { debug = false } = {}) {
  try {
    if (!isNative()) return;

    await afInit({ debug });

    // Firebase ID is optional — don't let it block or drop the event.
    let firebaseId = null;

    try {
      firebaseId = await Promise.race([
        getFirebaseAppInstanceId(),
        new Promise((resolve) => setTimeout(() => resolve(null), 1000)),
      ]);
    } catch {
      // Firebase id is optional, continue without it.
    }

    const eventValue = {
      ...values,
      ...(firebaseId ? { firebase_app_instance_id: firebaseId } : {}),
    };

    console.log("[AppsFlyer] logEvent", name, eventValue);
    return await AppsFlyer.logEvent({ eventName: name, eventValue });
  } catch (e) {
    console.warn("[AppsFlyer] logEvent failed", e);
  }
}

export async function afGetUid() {
  try {
    if (!isNative()) return null;
    const res = await AppsFlyer.getAppsFlyerUID();
    return res?.uid ?? null;
  } catch {
    return null;
  }
}

export async function trackPurchase({
  price,
  currency = "USD",
  plan = "smart_timing_monthly",
} = {}) {
  return afLogEvent("af_purchase", {
    af_revenue: Number(price),
    af_currency: currency,
    af_content_id: plan,
    af_content_type: "subscription",
  });
}

export async function trackSignup(method = "unknown") {
  return afLogEvent("af_complete_registration", {
    af_registration_method: method,
  });
}

async function logFirebaseCompatEvent(
  name,
  params = {}
) {
  const firebaseName = String(name || "")
    .replace(/^af_/, "")
    .slice(0, 40);

  if (!firebaseName) return;

  const eventParams = {
    ...params,
    app_surface: getPlatform(),
  };

  try {
    if (isNative()) {
      const { FirebaseAnalytics } = await import(
        "@capacitor-firebase/analytics"
      );

      await FirebaseAnalytics.logEvent({
        name: firebaseName,
        params: eventParams,
      });

      return;
    }

    const [
      { logEvent },
      { analytics },
    ] = await Promise.all([
      import("firebase/analytics"),
      import("../firebase"),
    ]);

    if (analytics) {
      logEvent(
        analytics,
        firebaseName,
        eventParams
      );
    }
  } catch (e) {
    console.warn(
      "[Analytics] Firebase event failed",
      firebaseName,
      e
    );
  }
}

export async function afTrack(
  name,
  params = {}
) {
  const clean = name.startsWith("af_")
    ? name
    : `af_${name}`;

  const enriched = {
    ...params,
    app_surface: getPlatform(),
  };

  if (!isNative()) {
    // Compatibility path for existing afTrack(...) calls:
    // send the same event to GA4 on web instead of silently dropping it.
    await logFirebaseCompatEvent(
      name,
      enriched
    );

    return;
  }

  // Native keeps AppsFlyer attribution AND mirrors the event to Firebase/GA4
  // so GA4 can compare Web / iOS / Android funnels in one place.
  const [appsFlyerResult] =
    await Promise.allSettled([
      afLogEvent(clean, enriched),
      logFirebaseCompatEvent(
        name,
        enriched
      ),
    ]);

  return appsFlyerResult.status === "fulfilled"
    ? appsFlyerResult.value
    : undefined;
}

export async function afGetMediaSource() {
  if (_mediaSource) return _mediaSource;

  try {
    const { value } = await Preferences.get({ key: MEDIA_SOURCE_KEY });
    const normalized = normalizeMediaSource(value);

    if (normalized) {
      _mediaSource = normalized;
    }
  } catch {}

  return _mediaSource; // null only if truly organic / unresolved
}

export async function afWaitForMediaSource({
  timeoutMs = 12000,
  pollMs = 500,
} = {}) {
  await afInit();

  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    const source = await afGetMediaSource();

    if (source && source !== "organic") {
      return source;
    }

    await new Promise((resolve) => {
      setTimeout(resolve, pollMs);
    });
  }

  return await afGetMediaSource();
}

export async function afGetAttribution() {
  if (!isNative()) {
    return afGetWebAttribution(
      typeof window !== "undefined"
        ? window.location.search
        : ""
    );
  }

  if (_attribution) {
    return _attribution;
  }

  try {
    const { value } =
      await Preferences.get({
        key: ATTRIBUTION_KEY,
      });

    if (value) {
      _attribution =
        JSON.parse(value);
    }
  } catch {}

  return _attribution;
}


export async function afWaitForAttribution({
  timeoutMs = 2500,
  pollMs = 250,
} = {}) {
  await afInit();

  const startedAt = Date.now();

  while (
    Date.now() - startedAt <
    timeoutMs
  ) {
    const attribution =
      await afGetAttribution();

    if (
      attribution?.media_source
    ) {
      return attribution;
    }

    await new Promise(
      (resolve) =>
        setTimeout(
          resolve,
          pollMs
        )
    );
  }

  return await afGetAttribution();
}

// Optional: useful in native Safari / Android logs while testing.
export async function afDebugAttribution() {
  const source = await afGetMediaSource();

  let uid = null;
  try {
    uid = await afGetUid();
  } catch {}

  const result = {
    source,
    uid,
    inited: _inited,
  };

  console.log("[AppsFlyer] debug attribution", result);
  return result;
}